import { useCallback, useState } from 'react';
import { Sparkles, ExternalLink, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import Input from '@/components/ui/Input';
import ImageUploader from '@/components/admin/ImageUploader';
import { useAiForgePopup, type AiForgeAssetPayload } from '@/hooks/useAiForgePopup';
import { SITE_NAME, SITE_TAGLINE, SITE_BRAND_SYMBOL } from '@/config/site';

interface BrandLogoSectionProps {
  /** Current store_logo_url. */
  value: string;
  /** Updates the parent settings state (saved with the page too). */
  onChange: (url: string) => void;
  storeName?: string;
  tagline?: string;
}

/**
 * Logo manager for the admin Settings "Brand" card: shows/upload the current
 * logo, generate one with the AI-Forge Logo designer, or paste a URL. Upload and
 * AI both persist immediately to the public `store_logo_url` setting and refresh
 * the settings query so the site header updates without a full page save.
 */
export default function BrandLogoSection({ value, onChange, storeName, tagline }: BrandLogoSectionProps) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist a chosen logo immediately + refresh the header's settings query.
  const persist = useCallback(
    async (url: string) => {
      onChange(url);
      try {
        await api.put('/admin/settings', { settings: { store_logo_url: url } });
        queryClient.invalidateQueries({ queryKey: ['settings'] });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save logo');
      }
    },
    [onChange, queryClient],
  );

  // AI-Forge returns the picked logo as a data/download URL — upload it through
  // the same image endpoint as a manual upload, then persist the resulting URL.
  const handleAssetReceived = useCallback(
    async (payload: AiForgeAssetPayload) => {
      setBusy(true);
      setError(null);
      try {
        const src = payload.dataUrl || payload.downloadUrl;
        if (!src) throw new Error('No image returned from AI-Forge');
        const blob = await (await fetch(src)).blob();
        const formData = new FormData();
        formData.append('file', blob, 'logo.png');
        const { url } = await api.upload<{ url: string }>(
          '/admin/upload/cms?width=512&height=512&fit=cover',
          formData,
        );
        await persist(url);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save the generated logo');
      } finally {
        setBusy(false);
      }
    },
    [persist],
  );

  // A compact brand brief seeds the AI-Forge Logo tool (the tenant can edit it).
  const buildBrief = (): string => {
    const name = storeName || SITE_NAME;
    const tag = tagline || SITE_TAGLINE;
    let primary = '';
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--color-sage-700').trim();
      if (/^#[0-9a-fA-F]{6}$/.test(v)) primary = ` Primary color ${v}.`;
    } catch {
      /* ignore */
    }
    const motif =
      SITE_BRAND_SYMBOL && SITE_BRAND_SYMBOL !== 'Sparkles' ? ` Brand motif hint: ${SITE_BRAND_SYMBOL}.` : '';
    return `Logo for ${name}${tag ? ` — ${tag}` : ''}.${primary}${motif} Design a single, iconic brand symbol.`;
  };

  const { openForge, isOpen } = useAiForgePopup({
    context: 'brand-logo',
    allowedTypes: ['image'],
    tool: 'logo',
    suggestedPrompt: buildBrief(),
    onAssetReceived: handleAssetReceived,
  });

  return (
    <div className="space-y-4">
      <ImageUploader value={value} onChange={persist} label="Logo" targetWidth={512} targetHeight={512} />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => openForge()}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Sparkles className="w-4 h-4" aria-hidden="true" />}
          {busy ? 'Saving…' : 'Generate with AI'}
        </button>
        {isOpen && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            Design your logo in the popup, then click &ldquo;Use as site logo&rdquo;.
          </span>
        )}
      </div>

      <Input
        label="Or paste a logo URL"
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://example.com/logo.png"
        helperText="Shown in your site header. Uploading or generating applies instantly; a pasted URL saves with the page."
      />

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
